import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, type FormikProps, useFormikContext } from "formik";

import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import {
  getGetDealOpportunityDataPointsQueryKey,
  useGetDealOpportunityDataPoints,
  useUpdateDealOpportunityDataPoints,
} from "@vega-api/generated/deal-opportunity-data-points/deal-opportunity-data-points";
import { type DealOpportunityDataPoint } from "@vega-api/generated/types/dealOpportunityDataPoint";
import { type DealOpportunityDataPointUpdate } from "@vega-api/generated/types/dealOpportunityDataPointUpdate";
import { DealOpportunityDataPointValueType } from "@vega-api/generated/types/dealOpportunityDataPointValueType";
import { ThemedDropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import {
  type BaseMultiSelectOption,
  DisplayMode,
  ThemedMultiselectDropdown,
} from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { DateField } from "@vega-core/components/Form/Fields/DateField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { NumberField } from "@vega-core/components/Form/Fields/NumberField";
import { RadioGroupBooleanField } from "@vega-core/components/Form/Fields/RadioGroupBooleanField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { DependentOptionsField } from "./DependentOptionsField";

type UpdateDataPointsModalProps = {
  dealOpportunityId: string;
  dealId: string;
  isOpen: boolean;
  onClose: () => void;
};

type FormValues = Record<string, string[] | string | undefined>;

const fieldArrayToFormValues = (
  dataPoints: DealOpportunityDataPoint[],
): FormValues =>
  dataPoints.reduce((formValues, point) => {
    formValues[point.name] =
      point.valueType === DealOpportunityDataPointValueType.MULTIPLE_SELECT
        ? (point.selectedOptions ?? [])
        : point.value;

    return formValues;
  }, {} as FormValues);

const formValuesToFieldArray = (
  values: FormValues,
): DealOpportunityDataPointUpdate[] =>
  Object.entries(values).map(([name, value]) => ({
    name,
    value: Array.isArray(value) ? value.join(",") : value,
  }));

const LoadingSkeleton = () => (
  <div className="w-dvw max-w-lg flex flex-col gap-6 items-start">
    {[1, 2, 3, 4, 5].map((number) => (
      <div className="flex flex-col gap-2 w-full" key={number}>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-10 w-full" />
      </div>
    ))}
  </div>
);

const MultiSelectField = ({
  dataPoint,
  disabled,
}: {
  dataPoint: DealOpportunityDataPoint;
  disabled: boolean;
}) => {
  const { name, displayName, placeholder, options } = dataPoint;
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const dropdownOptions: BaseMultiSelectOption[] = (options ?? []).map(
    (opt) => ({ label: opt, value: opt }),
  );

  const formValue = values[name];
  const dropdownSelectedOptions: BaseMultiSelectOption[] = Array.isArray(
    formValue,
  )
    ? formValue.map((opt) => ({ label: opt, value: opt }))
    : [];

  return (
    <ThemedMultiselectDropdown
      disabled={disabled}
      displayMode={DisplayMode.Tags}
      label={displayName}
      name={name}
      options={dropdownOptions}
      placeholder={placeholder}
      selectedOptions={dropdownSelectedOptions}
      setSelectedOptions={(items) =>
        setFieldValue(
          name,
          items.map((i) => i.value),
        )
      }
    />
  );
};

const renderFieldByType = (dataPoint: DealOpportunityDataPoint) => {
  const { name, displayName, valueType, placeholder, options, isEditable } =
    dataPoint;
  const disabled = isEditable !== true;

  switch (valueType) {
    case DealOpportunityDataPointValueType.TEXT_SHORT:
      return (
        <InputField
          disabled={disabled}
          key={name}
          label={displayName}
          name={name}
          placeholder={placeholder}
        />
      );

    case DealOpportunityDataPointValueType.TEXT_LONG:
      return (
        <TextareaField
          disabled={disabled}
          key={name}
          label={displayName}
          name={name}
          placeholder={placeholder}
        />
      );

    case DealOpportunityDataPointValueType.DATE:
      return (
        <DateField
          disabled={disabled}
          key={name}
          label={displayName}
          name={name}
        />
      );

    case DealOpportunityDataPointValueType.OPTIONS: {
      const dropdownOptions: OptionType[] = options
        ? options.map((option) => ({
            label: option,
            value: option,
          }))
        : [];

      return (
        <ThemedDropdownField
          disabled={disabled}
          isClearable={false}
          key={name}
          label={displayName}
          name={name}
          options={dropdownOptions}
          placeholder={placeholder}
        />
      );
    }

    case DealOpportunityDataPointValueType.DEPENDENT_OPTIONS:
      return (
        <DependentOptionsField
          dataPoint={dataPoint}
          disabled={disabled}
          key={name}
        />
      );

    case DealOpportunityDataPointValueType.BOOLEAN:
      return (
        <RadioGroupBooleanField
          disabled={disabled}
          key={name}
          label={displayName}
          name={name}
        />
      );

    case DealOpportunityDataPointValueType.NUMBER:
      return (
        <NumberField
          decimalScale={2}
          disabled={disabled}
          key={name}
          label={displayName}
          name={name}
          placeholder={placeholder}
        />
      );

    case DealOpportunityDataPointValueType.MULTIPLE_SELECT:
      return (
        <MultiSelectField
          dataPoint={dataPoint}
          disabled={disabled}
          key={name}
        />
      );
  }
};

export const UpdateDataPointsModal = ({
  dealId,
  dealOpportunityId,
  isOpen,
  onClose,
}: UpdateDataPointsModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const formRef = useRef<FormikProps<FormValues>>(null);

  const {
    data: dataPoints,
    isError: isDataPointsError,
    isLoading: isDataPointsLoading,
  } = useGetDealOpportunityDataPoints(dealOpportunityId, {
    query: {
      enabled: isOpen,
      select: (data) => data.data?.dataPoints,
    },
  });

  const { mutate: updateDataPoints, isPending: isUpdateDataPointsPending } =
    useUpdateDealOpportunityDataPoints({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.coInvestments_tracking_dataPoints_updateSuccess(),
          });
          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityDataPointsQueryKey(dealOpportunityId),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
          onClose();
        },
        onError: (error) => {
          showErrorToast(error);
        },
      },
    });

  const handleSubmit = (values: FormValues) => {
    const dataPointsRequest = formValuesToFieldArray(values);
    updateDataPoints({
      dealOpportunityId,
      data: {
        dataPoints: dataPointsRequest,
      },
    });
  };

  const areDataPointsEmpty =
    dataPoints !== undefined && dataPoints.length === 0;
  const hasEditableDataPoints =
    dataPoints?.some((dataPoint) => dataPoint.isEditable === true) ?? false;
  const isLoadingState = isDataPointsLoading || dataPoints === undefined;

  if (!isOpen) {
    return null;
  }

  if (isDataPointsError) {
    return (
      <ActionaryModal
        ModalButtonsGroupProps={{
          secondaryAction: onClose,
          secondaryActionLabel: m.btn_close(),
          secondaryActionText: m.btn_close(),
        }}
        dataTestId="update-data-points-modal"
        description={m.coInvestments_tracking_dataPoints_loadError()}
        fluidHeight
        open
        setOpen={onClose}
        title={m.coInvestments_tracking_dataPoints_modalTitle()}
      />
    );
  }

  if (isLoadingState || areDataPointsEmpty) {
    return (
      <ActionaryModal
        dataTestId="update-data-points-modal"
        fluidHeight
        open
        setOpen={onClose}
        title={m.coInvestments_tracking_dataPoints_modalTitle()}
      >
        <LoadingSkeleton />
      </ActionaryModal>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={fieldArrayToFormValues(dataPoints)}
      innerRef={formRef}
      onSubmit={handleSubmit}
    >
      {({ dirty }) => (
        <ActionaryModal
          ModalButtonsGroupProps={{
            mainActionLabel: m.btn_confirm(),
            mainAction: () => formRef.current?.submitForm(),
            mainActionText: m.btn_confirm(),
            disabled:
              isUpdateDataPointsPending || !hasEditableDataPoints || !dirty,
            secondaryAction: onClose,
            secondaryActionLabel: m.btn_cancel(),
            secondaryActionText: m.btn_cancel(),
            disabledSecondary: isUpdateDataPointsPending,
          }}
          dataTestId="update-data-points-modal"
          fluidHeight
          open
          setOpen={onClose}
          title={m.coInvestments_tracking_dataPoints_modalTitle()}
        >
          <Form className="w-dvw max-w-lg flex flex-col gap-6">
            {dataPoints.map((dataPoint) => renderFieldByType(dataPoint))}
          </Form>
        </ActionaryModal>
      )}
    </Formik>
  );
};
